/**
 * The one-time welcome (5 steps). Opens by itself on first login; "Do this later" saves it as skipped;
 * the X just closes it for now (it opens again next time). Help menu > "Show the welcome again" reopens it.
 * Every step is saved on the server as you go, so it resumes where you left off.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight, CalendarCheck, CheckCircle2, ClipboardCopy, FileUp, Send, Sparkles, X } from 'lucide-react';
import type { OnboardingState, SaveStepRequest } from '@bs/contracts';
import { Button } from '../components/ui/button.js';
import { Input } from '../components/ui/input.js';
import { Checkbox } from '../components/ui/checkbox.js';
import { InlineAlert } from '../components/ui/states.js';
import { AreaMultiSelect, defaultAreaIds } from '../components/ui/area-multi-select.js';
import { useSession } from '../lib/session.js';
import { Field, HelpTip, Titled, type HelpKey } from '../modules/review/HelpTip.js';
import { notify, notifyError } from '../lib/toast.js';
import { errorMessage } from '../api/client.js';
import { cn } from '../lib/utils.js';
import { onbCall, ONB_KEY } from './onboardingApi.js';
import { addClient, MAX_QUICK, rowsFromCsv, type AddResult, type ClientRow } from './quickAdd.js';

export const STEP_TITLES = ['Hello', 'Your businesses', 'First clients', 'Your day', 'Done'] as const;

interface Props {
  state: OnboardingState;
  meId: string;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  /** Called after the last step: start the short walk through the menu ('welcome-next'). */
  onStartTour: () => void;
}

export function WelcomeWizard({ state, meId, open, onOpenChange, onStartTour }: Props) {
  const qc = useQueryClient();
  const [step, setStep] = useState(state.currentStep);
  const opened = useRef(false);
  useEffect(() => {
    if (open && !opened.current) setStep(state.currentStep);
    opened.current = open;
  }, [open, state.currentStep]);

  const setData = (s: OnboardingState) => qc.setQueryData(ONB_KEY, s);
  // The welcome adds people and areas: every page's cached numbers are now old (BS-074).
  const refreshAll = () => void qc.invalidateQueries({ predicate: (q) => q.queryKey[0] !== ONB_KEY[0] });
  const save = useMutation({
    mutationFn: (b: SaveStepRequest) => onbCall('saveOnboardingStep', { body: b }),
    onSuccess: (s, b) => {
      setData(s);
      if (b.step === 'hello') void qc.invalidateQueries({ queryKey: ['me'] });
      else refreshAll();
      setStep((n) => Math.min(5, n + 1));
    },
    onError: notifyError,
  });
  const finish = useMutation({
    mutationFn: (skipped: boolean) => onbCall('finishOnboarding', { body: { skipped } }),
    onSuccess: (s, skipped) => {
      setData(s);
      refreshAll();
      onOpenChange(false);
      if (skipped) notify('Okay. You can open the welcome again from the Help menu (?) at the top.', 'info');
      else onStartTour();
    },
    onError: notifyError,
  });

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-slate-950/50 backdrop-blur-[1px] animate-fade-in" />
        <Dialog.Content aria-describedby="onb-desc"
          className="fixed left-1/2 top-1/2 z-50 flex max-h-[92dvh] w-[calc(100vw-2rem)] max-w-2xl -translate-x-1/2 -translate-y-1/2 flex-col rounded-xl border bg-card shadow-pop focus:outline-none">
          <div className="flex items-start justify-between gap-4 border-b px-5 py-4">
            <div className="min-w-0">
              <Dialog.Title className="text-base font-semibold"><Titled k="onb.wizard">Welcome to Business Sense</Titled></Dialog.Title>
              <Dialog.Description id="onb-desc" className="mt-0.5 text-sm text-muted-foreground">Step {step} of 5: {STEP_TITLES[step - 1]}</Dialog.Description>
            </div>
            <Dialog.Close className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground" aria-label="Close for now">
              <X className="h-5 w-5" />
            </Dialog.Close>
          </div>
          <Progress step={step} />
          <div className="flex-1 overflow-y-auto px-5 py-5">
            {step === 1 && <HelloStep state={state} busy={save.isPending} onSave={(data) => save.mutate({ step: 'hello', data })} />}
            {step === 2 && <BusinessesStep state={state} busy={save.isPending} onSave={(data) => save.mutate({ step: 'businesses', data })} />}
            {step === 3 && <ClientsStep state={state} meId={meId} busy={save.isPending} onSave={(data) => save.mutate({ step: 'clients', data })} />}
            {step === 4 && <DayStep busy={save.isPending} onSave={() => save.mutate({ step: 'day', data: { seen: true } })} />}
            {step === 5 && <DoneStep busy={finish.isPending} onFinish={() => finish.mutate(false)} />}
          </div>
          <div className="flex items-center justify-between gap-2 border-t px-5 py-3">
            <Button variant="ghost" disabled={step === 1} onClick={() => setStep((n) => Math.max(1, n - 1))}><ArrowLeft /> Back</Button>
            <Button variant="link" loading={finish.isPending} onClick={() => finish.mutate(true)}>Do this later</Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function Progress({ step }: { step: number }) {
  return (
    <ol className="flex gap-1.5 px-5 pt-3" aria-label="Progress">
      {STEP_TITLES.map((t, i) => (
        <li key={t} className="flex-1" aria-current={i + 1 === step ? 'step' : undefined}>
          <span className={cn('block h-1.5 rounded-full', i + 1 <= step ? 'bg-primary' : 'bg-muted')} />
          <span className="mt-1 hidden text-[11px] text-muted-foreground sm:block">{t}</span>
        </li>
      ))}
    </ol>
  );
}

function StepHead({ title, text, help }: { title: string; text: string; help: HelpKey }) {
  return (
    <div className="mb-4">
      <h2 className="text-lg font-semibold"><Titled k={help}>{title}</Titled></h2>
      <p className="mt-1 text-sm text-muted-foreground">{text}</p>
    </div>
  );
}

function NextButton({ busy, onClick, children = 'Save and continue', disabled }: { busy: boolean; onClick: () => void; children?: ReactNode; disabled?: boolean }) {
  return <div className="mt-6 flex justify-end"><Button loading={busy} disabled={disabled} onClick={onClick}>{children} <ArrowRight /></Button></div>;
}

// ---------------------------------------------------------------- 1 hello
function HelloStep({ state, busy, onSave }: { state: OnboardingState; busy: boolean; onSave: (d: { name: string; whatsapp: string; briefTime: string }) => void }) {
  const [name, setName] = useState(state.hello.name);
  const [whatsapp, setWhatsapp] = useState(state.hello.whatsapp);
  const [briefTime, setBriefTime] = useState(state.hello.briefTime);
  const first = name.trim().split(/\s+/)[0] || 'there';
  const phoneBad = whatsapp.trim() !== '' && !/^\+[1-9]\d{6,14}$/.test(whatsapp.replace(/\s/g, ''));
  return (
    <div>
      <StepHead title={`Hi ${first}!`} help="onb.wizard" text="Let us check a few things so reminders reach you at the right time." />
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Your name" help="onb.name"><Input value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" /></Field>
        <Field label="Your WhatsApp number" help="onb.whatsapp" hint="Start with +91. Only for reminders to you." error={phoneBad ? 'Type it like +919812345678' : null}>
          <Input value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} placeholder="+919812345678" inputMode="tel" />
        </Field>
        <Field label="Morning brief time (India time)" help="onb.brief"><Input type="time" value={briefTime} onChange={(e) => setBriefTime(e.target.value)} /></Field>
      </div>
      <NextButton busy={busy} disabled={!name.trim() || phoneBad || !briefTime}
        onClick={() => onSave({ name: name.trim(), whatsapp: whatsapp.replace(/\s/g, ''), briefTime })} />
    </div>
  );
}

// ---------------------------------------------------------------- 2 businesses
function BusinessesStep({ state, busy, onSave }: { state: OnboardingState; busy: boolean; onSave: (d: { areas: { verticalId: string; productIds: string[] }[] }) => void }) {
  const [picked, setPicked] = useState<Record<string, string[]>>(() => Object.fromEntries(state.areas.map((a) => [a.verticalId, a.selectedProductIds])));
  const toggle = (areaId: string, pid: string, on: boolean) =>
    setPicked((p) => ({ ...p, [areaId]: on ? [...(p[areaId] ?? []), pid] : (p[areaId] ?? []).filter((x) => x !== pid) }));
  return (
    <div>
      <StepHead title="Your businesses" help="onb.areas" text="These are the businesses you work in. Tick what you personally sell." />
      {state.areas.length === 0 && <InlineAlert tone="info">You are not in any business area yet. Ask a colleague to add you from the Users page.</InlineAlert>}
      <div className="space-y-3">
        {state.areas.map((a) => (
          <section key={a.verticalId} className="rounded-lg border p-4" aria-label={a.name}>
            <h3 className="font-semibold">{a.name}</h3>
            <p className="mt-0.5 text-sm text-muted-foreground">{a.description || 'No description yet. It can be added in Business setup.'}</p>
            <p className="mt-3 text-xs font-medium uppercase tracking-wide text-muted-foreground"><Titled k="onb.products">What you sell</Titled></p>
            {a.products.length === 0 ? <p className="mt-1 text-sm text-muted-foreground">No products listed yet for this business.</p> : (
              <ul className="mt-2 grid gap-2 sm:grid-cols-2">
                {a.products.map((p) => {
                  const id = `onb-p-${p.id}`;
                  return (
                    <li key={p.id} className="flex items-center gap-2">
                      <Checkbox id={id} checked={(picked[a.verticalId] ?? []).includes(p.id)} onCheckedChange={(v) => toggle(a.verticalId, p.id, v === true)} />
                      <label htmlFor={id} className="text-sm">{p.name}{p.tier ? <span className="text-muted-foreground"> ({p.tier})</span> : null}</label>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        ))}
      </div>
      <NextButton busy={busy} onClick={() => onSave({ areas: state.areas.map((a) => ({ verticalId: a.verticalId, productIds: picked[a.verticalId] ?? [] })) })} />
    </div>
  );
}

// ---------------------------------------------------------------- 3 clients
function ClientsStep({ state, meId, busy, onSave }: { state: OnboardingState; meId: string; busy: boolean; onSave: (d: { added: number; skipped: boolean }) => void }) {
  const { vertical } = useSession();
  const areaOptions = state.areas.map((a) => ({ value: a.verticalId, label: a.name, key: a.key }));
  // Default tick: the area chosen in the top bar, else the user's only area (else none: the user ticks).
  const defaultAreas = defaultAreaIds(vertical, areaOptions);
  const blank = (): ClientRow => ({ name: '', company: '', phone: '', areaIds: [...defaultAreas] });
  const [rows, setRows] = useState<ClientRow[]>(() => Array.from({ length: 3 }, blank));
  const [results, setResults] = useState<AddResult[] | null>(null);
  const [problems, setProblems] = useState<string[]>([]);
  const [adding, setAdding] = useState(false);
  const filled = rows.filter((r) => r.name.trim());
  const set = (i: number, patch: Partial<ClientRow>) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  const addAll = async (list: ClientRow[]) => {
    setAdding(true);
    try {
      const out: AddResult[] = [];
      for (const r of list) out.push(await addClient(r, meId)); // one by one: each is dedupe-checked by the server
      setResults(out);
      const added = out.filter((x) => x.ok).length;
      notify(`${added} of ${out.length} added to your Pipeline.`, added ? 'ok' : 'info');
      return added;
    } finally {
      setAdding(false);
    }
  };

  const onFile = async (f: File) => {
    try {
      const { rows: csvRows, problems: p } = rowsFromCsv(await f.text(), state.areas, defaultAreas.length ? defaultAreas : areaOptions.slice(0, 1).map((o) => o.value));
      setProblems(p);
      if (csvRows.length) await addAll(csvRows);
    } catch (e) {
      console.error('csv import failed', e);
      setProblems([`The file could not be read: ${errorMessage(e)}`]);
    }
  };

  const doneCount = results?.filter((r) => r.ok).length ?? 0;
  return (
    <div>
      <StepHead title="Add your first clients" help="onb.clients" text="Add up to five people you are talking to now, or import a file. You can also skip this." />
      {state.areas.length === 0 ? <InlineAlert tone="info">You need a business area before you can add clients.</InlineAlert> : (
        <>
          <div className="space-y-3">
            {rows.map((r, i) => (
              <fieldset key={i} className="grid gap-2 rounded-lg border p-3 sm:grid-cols-3" aria-label={`Client ${i + 1}`}>
                <Field label="Name" help="onb.client.name"><Input value={r.name} onChange={(e) => set(i, { name: e.target.value })} placeholder="Asha Rao" /></Field>
                <Field label="Company" help="onb.client.company"><Input value={r.company} onChange={(e) => set(i, { company: e.target.value })} placeholder="Sunrise School" /></Field>
                <Field label="Phone" help="onb.client.phone"><Input value={r.phone} onChange={(e) => set(i, { phone: e.target.value })} placeholder="98111 22233" inputMode="tel" /></Field>
                <div className="sm:col-span-3"><Field label="Businesses" help="onb.client.area"><AreaMultiSelect id={`onb-areas-${i}`} aria-label={`Businesses for client ${i + 1}`} options={areaOptions} value={r.areaIds} onChange={(v) => set(i, { areaIds: v })} /></Field></div>
              </fieldset>
            ))}
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" disabled={rows.length >= MAX_QUICK} onClick={() => setRows((rs) => [...rs, blank()])}>Add another row</Button>
            <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm hover:bg-muted">
              <FileUp className="h-4 w-4" aria-hidden /> Import a CSV file
              <input type="file" accept=".csv,text/csv" className="sr-only" onChange={(e) => { const f = e.target.files?.[0]; if (f) void onFile(f); e.target.value = ''; }} />
            </label>
            <HelpTip k="onb.csv" />
            <Button size="sm" loading={adding} disabled={filled.length === 0 || filled.some((r) => r.areaIds.length === 0)} onClick={() => void addAll(filled).then(() => setRows([blank()]))}>Add {filled.length || ''} {filled.length === 1 ? 'person' : 'people'}</Button>
          </div>
          {problems.length > 0 && <InlineAlert tone="warning" className="mt-3"><ul className="list-disc pl-4">{problems.map((p) => <li key={p}>{p}</li>)}</ul></InlineAlert>}
          {results && (
            <ul className="mt-3 space-y-1 text-sm" aria-label="Results">
              {results.map((r, i) => <li key={i} className={r.ok ? 'text-success' : 'text-danger'}>{r.row.name}: {r.message}</li>)}
            </ul>
          )}
        </>
      )}
      <div className="mt-6 flex justify-end gap-2">
        <Button variant="outline" disabled={busy || adding} onClick={() => onSave({ added: doneCount, skipped: true })}>Skip this step</Button>
        <Button loading={busy} disabled={adding} onClick={() => onSave({ added: doneCount, skipped: doneCount === 0 })}>Continue <ArrowRight /></Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- 4 day
const DAY_CARDS = [
  { icon: CalendarCheck, title: '1. Open Today', text: 'Every morning, Today shows who needs you, best first.' },
  { icon: ClipboardCopy, title: '2. Copy the message', text: 'Read the suggested message, change it if you like, and press Copy.' },
  { icon: Send, title: '3. Send it, then "I sent it"', text: 'Send it from your own WhatsApp or email. Then press "I sent it" so your history stays right.' },
];
function DayStep({ busy, onSave }: { busy: boolean; onSave: () => void }) {
  return (
    <div>
      <StepHead title="How your day works" help="onb.day" text="Three simple steps, every day." />
      <ol className="grid gap-3 sm:grid-cols-3">
        {DAY_CARDS.map((c) => (
          <li key={c.title} className="rounded-lg border bg-muted/30 p-4 text-center">
            <span className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary" aria-hidden><c.icon className="h-7 w-7" /></span>
            <p className="font-semibold">{c.title}</p>
            <p className="mt-1 text-sm text-muted-foreground">{c.text}</p>
          </li>
        ))}
      </ol>
      <p className="mt-4 text-sm text-muted-foreground">Business Sense never sends messages to your clients. You always send them yourself.</p>
      <NextButton busy={busy} onClick={onSave}>Got it</NextButton>
    </div>
  );
}

// ---------------------------------------------------------------- 5 done
function DoneStep({ busy, onFinish }: { busy: boolean; onFinish: () => void }) {
  return (
    <div className="py-4 text-center">
      <CheckCircle2 className="mx-auto h-12 w-12 text-success" aria-hidden />
      <h2 className="mt-3 text-lg font-semibold">You are all set</h2>
      <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">Next, a short tour shows you where your daily work lives. It takes one minute.</p>
      <div className="mt-6 flex justify-center"><Button loading={busy} onClick={onFinish}><Sparkles /> Start the tour</Button></div>
    </div>
  );
}
